import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { usePortalAuth } from '../../hooks/usePortalAuth'
import { useEnrollmentsForPortalUser } from '../../hooks/useEnrollmentsForPortalUser'
import { useActivitiesByIds } from '../../hooks/useActivitiesByIds'
import { usePortalQueryClientId } from '../../hooks/portalHostQueryGate'
import { useMounted } from '../../hooks/useMounted'
import { getClient } from '../../libs/DBApiHandler'
import { Client, Enrollment } from '../../../../pure-js/types/GrooverTypes'
import { buildRoute } from '../../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../../pure-js/libs/RoutePath'
import useAppState from '../../hooks/useAppState'
import { formatGrooverText, getGrooverText } from '../../libs/GrooverTextRepository'
import {
  applyLiveActivitiesToEnrollment,
  countSchoolEnrollments,
  getEnrollmentActivityIds,
  getPortalCourseEnrollments,
  groupPortalEnrollmentsBySchoolAndPeriod
} from './portalEnrollmentHelpers'
import { PortalEnrollmentCard } from './PortalEnrollmentCard'
import { PortalEnrollmentDetailCard } from './PortalEnrollmentDetailCard'
import {
  EmptyDescription,
  EmptyIcon,
  EmptyState,
  EmptyTitle,
  EnrollmentList,
  ErrorContainer,
  ErrorIcon,
  ErrorText,
  GroupCount,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  MainContent,
  PageContainer,
  PageCount,
  PageHeader,
  PageTitle,
  PeriodHeader,
  PeriodName,
  PeriodSection,
  RetryButton,
  SchoolHeader,
  SchoolName,
  SchoolSection
} from './PortalEnrollmentsPage.styles'

const toDomId = (...parts: string[]): string => parts.join('-').replace(/[^a-zA-Z0-9_-]+/g, '-')

export const PortalEnrollmentsPage: React.FC = () => {
  const { portalUser, portalClaimsReady, isViewAs } = usePortalAuth()
  const { state } = useAppState()
  const navigate = useNavigate()
  const { enrollmentId } = useParams<{ enrollmentId?: string }>()
  const selectedLanguage = state.selectedLanguage
  const getText = useCallback((key: string) => getGrooverText(key, selectedLanguage), [selectedLanguage])
  const locale = selectedLanguage === 'sv' ? 'sv-SE' : selectedLanguage === 'no' ? 'nb-NO' : 'en-US'
  const isMounted = useMounted()
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null)
  const [clients, setClients] = useState<Record<string, Client | null>>({})

  const enrollmentUserId = portalClaimsReady ? (portalUser?.enrollmentUserId ?? null) : null
  const clientId = usePortalQueryClientId()
  const { data: enrollments, isLoading, error } = useEnrollmentsForPortalUser(enrollmentUserId, clientId)
  const visibleEnrollments = useMemo(() => getPortalCourseEnrollments(enrollments), [enrollments])
  const activityIds = useMemo(() => getEnrollmentActivityIds(visibleEnrollments), [visibleEnrollments])
  const liveActivities = useActivitiesByIds(activityIds)
  const displayEnrollments = useMemo(
    () => visibleEnrollments.map((enrollment) => applyLiveActivitiesToEnrollment(enrollment, liveActivities)),
    [visibleEnrollments, liveActivities]
  )
  const formatEnrollmentCount = useCallback(
    (count: number) =>
      count === 1
        ? getText('portalEnrollmentCountOne')
        : formatGrooverText(getText('portalEnrollmentCountMany'), [String(count)]),
    [getText]
  )
  const enrollmentGroups = useMemo(
    () =>
      groupPortalEnrollmentsBySchoolAndPeriod(displayEnrollments, clients, {
        unknownSchool: getText('portalEnrollmentUnknownSchool'),
        noPeriod: getText('portalEnrollmentNoPeriod')
      }),
    [displayEnrollments, clients, getText]
  )

  useEffect(() => {
    const clientIds = [...new Set(visibleEnrollments.map((e) => e.clientId).filter(Boolean))]
    for (const cid of clientIds) {
      if (cid in clients) continue
      getClient(cid, isMounted)
        .then((c) => {
          if (!isMounted()) return
          setClients((prev) => ({ ...prev, [cid]: c ?? null }))
        })
        .catch(() => {
          if (!isMounted()) return
          setClients((prev) => ({ ...prev, [cid]: null }))
        })
    }
  }, [visibleEnrollments, clients, isMounted])

  useEffect(() => {
    if (!enrollmentId || !displayEnrollments.length) return
    const match = displayEnrollments.find((e) => e.id === enrollmentId)
    if (match) {
      setSelectedEnrollment(match)
    } else {
      navigate(buildRoute(RoutePath.PORTAL_ENROLLMENTS), { replace: true })
    }
  }, [enrollmentId, displayEnrollments, navigate])

  const openEnrollment = useCallback(
    (enrollment: Enrollment) => {
      setSelectedEnrollment(enrollment)
      navigate(buildRoute(RoutePath.PORTAL_ENROLLMENT_DETAIL, { enrollmentId: enrollment.id }))
    },
    [navigate]
  )

  const closeEnrollment = useCallback(() => {
    setSelectedEnrollment(null)
    navigate(buildRoute(RoutePath.PORTAL_ENROLLMENTS))
  }, [navigate])

  useEffect(() => {
    if (!selectedEnrollment) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeEnrollment()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [selectedEnrollment, closeEnrollment])

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>{getText('portalEnrollmentsLoading')}</LoadingText>
        </LoadingContainer>
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorText>{getText('portalFailedToLoadEnrollments')}</ErrorText>
          <RetryButton onClick={() => window.location.reload()}>{getText('portalTryAgain')}</RetryButton>
        </ErrorContainer>
      </PageContainer>
    )
  }

  if (visibleEnrollments.length === 0) {
    return (
      <PageContainer>
        <MainContent>
          <PageHeader>
            <PageTitle>{getText('portalYourEnrollments')}</PageTitle>
          </PageHeader>
          <EmptyState>
            <EmptyIcon>📋</EmptyIcon>
            <EmptyTitle>{getText('portalNoEnrollmentsTitle')}</EmptyTitle>
            <EmptyDescription>{getText('portalNoEnrollments')}</EmptyDescription>
          </EmptyState>
        </MainContent>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <MainContent>
        <PageHeader>
          <PageTitle>{getText('portalYourEnrollments')}</PageTitle>
          <PageCount>{formatEnrollmentCount(visibleEnrollments.length)}</PageCount>
        </PageHeader>
        {enrollmentGroups.map((school) => {
          const schoolCountLabel = formatEnrollmentCount(countSchoolEnrollments(school))
          const schoolHeadingId = toDomId('school', school.schoolKey)
          return (
            <SchoolSection key={school.schoolKey} aria-label={`${school.schoolName}, ${schoolCountLabel}`}>
              <SchoolHeader>
                <SchoolName id={schoolHeadingId}>{school.schoolName}</SchoolName>
                <GroupCount aria-hidden>{schoolCountLabel}</GroupCount>
              </SchoolHeader>
              {school.periods.map((period) => {
                const periodHeadingId = toDomId('period', school.schoolKey, period.periodKey)
                const periodCountLabel = formatEnrollmentCount(period.enrollments.length)
                return (
                  <PeriodSection
                    key={period.periodKey}
                    aria-label={`${school.schoolName}, ${period.periodName}, ${periodCountLabel}`}
                  >
                    <PeriodHeader>
                      <PeriodName id={periodHeadingId}>{period.periodName}</PeriodName>
                      <GroupCount aria-hidden>{periodCountLabel}</GroupCount>
                    </PeriodHeader>
                    <EnrollmentList>
                      {period.enrollments.map((enrollment) => (
                        <PortalEnrollmentCard
                          key={enrollment.id}
                          enrollment={enrollment}
                          client={clients[enrollment.clientId] ?? null}
                          getText={getText}
                          showSchool={false}
                          enrollmentUserId={enrollmentUserId ?? undefined}
                          lang={selectedLanguage === 'sv' ? 'sv' : selectedLanguage === 'no' ? 'no' : 'en'}
                          isViewAs={isViewAs}
                          onClick={() => openEnrollment(enrollment)}
                        />
                      ))}
                    </EnrollmentList>
                  </PeriodSection>
                )
              })}
            </SchoolSection>
          )
        })}
      </MainContent>

      {selectedEnrollment && enrollmentUserId && (
        <PortalEnrollmentDetailCard
          enrollment={applyLiveActivitiesToEnrollment(selectedEnrollment, liveActivities)}
          client={clients[selectedEnrollment.clientId] ?? null}
          enrollmentUserId={enrollmentUserId}
          getText={getText}
          locale={locale}
          isViewAs={isViewAs}
          onClose={closeEnrollment}
        />
      )}
    </PageContainer>
  )
}
